// predictive traits types.ts

import type {
  DecisionEngineCondition,
  ModelConfiguration,
} from "../customer-data/decision-engine/types";
import type {
  EventCondition,
  EventFilter,
  IntervalUnit,
} from "../query/visual/types";
import type { Static } from "@sinclair/typebox";
import type { InputFeatureSchema } from "../customer-data/decision-engine/input-feature-schema";

export enum DecisionEngineColumnType {
  INT = "integer",
  FLOAT = "float",
  CATEGORICAL = "categorical",
  BOOL = "boolean",
  PASSTHROUGH = "passthrough",
  SPARSE_COUNT = "sparse_count",
  DATETIME = "datetime",
}

export enum PredictorRunStatus {
  Pending = "pending",
  Running = "running",
  Success = "success",
  Error = "error",
  Cancelled = "cancelled",
}

export enum PredictorStatus {
  INITIALIZING = "initializing",
  INGESTING_EVENT_DATA = "ingesting_event_data",
  BUILDING_TRAINING_DATA = "building_training_data",
  BUILT_TRAINING_DATA = "built_training_data",
  TRAINING = "training",
  LIVE = "live",
  ERRORED = "errored",
}

export enum EventLabel {
  ENGAGEMENT = "engagement",
  INTERACTION = "interaction",
  INTENT = "intent",
  REVENUE = "revenue",
  LIFECYCLE_MARKETING = "lifecycle_marketing",
}

export const DEFAULT_LABEL = EventLabel.INTENT;

export enum CachedEventPullType {
  Relationship = "relationship",
  Catalog = "catalog",
}

export enum PredictorType {
  PROPENSITY = "propensity",
  CATALOG_RECOMMENDATION = "catalog_recommendation",
  LOOKALIKE = "lookalike",
}

export enum PredictorOutcomeType {
  OCCURRENCE = "occurrence",
  VALUE = "value",
}

// Source of truth for aggregations the trainer supports for value outcomes.
// Deliberately narrower than the audience trait TraitType options - the
// frontend must render its picker from this enum, not from TraitType.
export enum PredictorOutcomeAggregation {
  SUM = "sum",
  COUNT = "count",
  MAX = "max",
}

// Stored in predictor_outcomes.definition (jsonb). NULL/undefined means the
// default binary "occurrence" behavior; "value" predicts the selected
// aggregation of a numeric event column over the outcome timeframe. Today
// only value definitions are persisted (occurrence is stored as NULL), but
// the discriminant is kept so occurrence variants can carry config later.
export type PredictorOutcomeDefinition =
  | { type: PredictorOutcomeType.OCCURRENCE }
  | {
      type: PredictorOutcomeType.VALUE;
      value_column: string;
      aggregation: PredictorOutcomeAggregation;
    };

export type CachedEventPullRelationshipConfig = {
  relationship_id: number;
  filter?: EventCondition | EventFilter;
  aggregations: {
    // Numeric columns to aggregate over in addition to counts of the event (e.g. revenue)
    numericColumns?: string[];
  };
  // this may live on CachedEventPullConfig or even the full table in the future
  // putting it here for now since it gets re-created on every pull so moving it is easy
  latestColumnTypes?: { [x: string]: DecisionEngineColumnType };
};

export type CachedEventPullCatalogConfig = {
  user_relationship_id: number;
  catalog_relationship_id: number;
  filter?: EventCondition | EventFilter;
  aggregations: {
    // Numeric columns to aggregate over in addition to counts of the event (e.g. revenue)
    numericColumns?: string[];
  };
  weight: number;
  // this may live on CachedEventPullConfig or even the full table in the future
  // putting it here for now since it gets re-created on every pull so moving it is easy
  latestColumnTypes?: { [x: string]: DecisionEngineColumnType };
};

export type CachedEventPullConfig = {
  relationship?: CachedEventPullRelationshipConfig;
  catalog?: CachedEventPullCatalogConfig;
  maxPullWindowDays?: number;
  bufferedWatermarkDays?: number;
  eventLabel?: EventLabel;
};

export interface BaseTableColumnDefinition {
  name: string;
  type: DecisionEngineColumnType;
}

// Corresponds to the "config" column on the predictors table. Later, we may have separate types based on the "type" of
// the predictor.
export type StaleWatermarkStrategy =
  | "throw"
  | "pull-incremental"
  | "skip-incremental";

export type PredictorConfig = {
  inferenceCadence: "daily" | "weekly" | "monthly";
  inferenceCronExpression?: string;
  demo_data?: TrainModelDemoMetadata;
  is_demo?: boolean;
  trainingMatrixMaxRows?: number;
  maxUsersForTraining?: number;
  lookalikeEligibleSampleSize?: number;
  inferenceBatchSize?: number;
  lookbackDays?: number;
  catalogTopK?: number;
  catalogLookbackDays?: number;
  watermarkStrategy?: StaleWatermarkStrategy;
};

// Stored in the predictors.suppression_filter column (jsonb).
// Empty object `{}` means no suppression filter is configured.
export type SuppressionFilterColumn = {
  keyEventId: string;
  eventFilter: EventFilter;
};

// Configurations below this are mostly for node <> python interfaces
export type KeyEvent = {
  eventModelId: number;
  filter: EventFilter;
  aggregations: {
    // Numeric columns to aggregate over in addition to counts of the event (e.g. revenue)
    numericColumns?: string[];
  };
};

// Represents _all_ the input features to a given Predictor. Consists of a list of columns and key events.
export type FeatureDefinition = {
  name: string;
  id: string;
  propertyFeatures: { modelId: number; columnName: string }[];
  keyEvents: KeyEvent[];
};

export type Event = {
  timestamp: number;
};

export interface TargetEvent {
  event_type?: "relationship_event";
  label: string;
  data_directory: string;
  aggregations: AggregationConfig;
  event_schema?: SchemaConfig;
  incremental_ndjson_data_path?: string;
  lookforward_window?: number;
  // if set this target event will behave as a churn predictor
  // churn is defined as the target event NOT happening for a churn_inactivity_days number of days
  churn_inactivity_days?: number;
  // undefined means the default binary "occurrence" behavior; "value" makes the
  // Python side build a continuous label from the selected column + aggregation
  definition?: PredictorOutcomeDefinition;
}

export interface AggregationConfig {
  numeric_columns: string[];
}

export interface SchemaConfig {
  [key: string]: DecisionEngineColumnType;
}

export interface KeyEventConfig {
  event_type: "relationship_event";
  identifier: string;
  label: string;
  data_directory: string;
  aggregations: AggregationConfig;
  event_schema: SchemaConfig;
  incremental_ndjson_data_path?: string;
}

export interface CatalogKeyEventConfig {
  event_type: "catalog_event";
  identifier: string;
  weight: number;
  data_directory: string;
  event_schema: SchemaConfig;
  incremental_ndjson_data_path?: string;
  aggregations: {
    numeric_columns: string[];
  };
}

export interface PredictiveTraitsBaseConfig {
  feature_key_event: KeyEventConfig;
}

export interface ConvertEventsToParquetConfig {
  feature_key_event: KeyEventConfig | CatalogKeyEventConfig;
  output_parquet_directory: string;
}

export interface TrainConfig extends PredictiveTraitsBaseConfig {
  target_event: TargetEvent;
}

export interface TrainModelConfig {
  training_matrix_dir: string;
  model_output_path: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  ml_config: ModelConfiguration;
  start_at?: number;
}

export interface TrainModelMetadata {
  config: Record<string, any>;
  training_results?: Record<string, any>;
  reward_model?: string;
  feature_scores_type?: string;
  feature_scores?: Record<string, any>;
  final_feature_names?: string[];
  metrics?: Record<string, any>;
  quantiles?: Quantile[];
  label_frequency?: number;
}

// For demo predictors, only the outcome is stored in the database.
// Static demo fields (num_users, finished_at, reward_model, quantiles)
// are loaded from default.json on the frontend.
export type TrainModelDemoMetadata = Partial<
  Pick<TrainModelMetadata, "reward_model" | "quantiles">
> & {
  predictorType?: "propensity" | "catalog_recommendation" | "lookalike";
  num_users?: number;
  finished_at?: string;
  outcome?: {
    name: string;
    timeframe: {
      quantity: number;
      unit: string;
      is_churn?: boolean;
    };
    relationship_id?: string;
    filter?: any;
    definition?: PredictorOutcomeDefinition;
  };
};

export interface BuildTrainingMatrixConfig {
  feature_key_events: KeyEventConfig[];
  target_event: TargetEvent;
  users_file_path: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  output_directory: string;
  start_date: string; // ISO date string
  end_date: string; // ISO date string
  filters?: DecisionEngineCondition;
  start_at?: number; // Unix timestamp in seconds
}

export interface BuildCatalogTrainingMatrixConfig {
  feature_key_events: KeyEventConfig[]; // Catalog interaction events
  users_file_path: string; // Path to user data file
  user_schema: Static<typeof InputFeatureSchema>[];
  catalog_file_path: string; // Path to catalog items file
  catalog_schema: Static<typeof InputFeatureSchema>[]; // Catalog item schema
  output_directory: string;
  start_date: string; // ISO date string
  end_date: string; // ISO date string
  filters?: DecisionEngineCondition; // Audience filters
  start_at?: number; // Unix timestamp in seconds
}

export type Quantile = {
  percentile: number;
  score: number;
};

export interface InferenceConfig {
  feature_key_events: KeyEventConfig[];
  target_event: TargetEvent;
  users_file_path: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  model_input_path: string;
  inference_output_path: string;
  quantiles: Quantile[];
  filters?: DecisionEngineCondition;
  inference_batch_size?: number;
  start_at?: number; // Unix timestamp in seconds
}

export interface BuildTrainingMatrixMetadata {
  config: Record<string, any>; // The configuration used for this build run
  training_matrix_files: string[]; // List of paths to the individual training matrix files that were created
  date_ranges: Array<[string, string]>; // List of date ranges corresponding to each training matrix file (ISO date strings)
  total_chunks: number; // Total number of chunks created
  last_processed_date?: string; // The last date that was actually processed (included in a complete chunk) - ISO date string
  metrics: Record<string, any>; // Timing and memory metrics for this build run
}

export interface OutcomeTimeframe {
  quantity: number;
  unit: IntervalUnit;
  is_churn?: boolean;
}

// Catalog recommendation types
export interface CatalogBuildTrainingMatrixConfig {
  output_directory: string;
  events: CatalogKeyEventConfig[];
  users_file_path: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  catalog_file_path: string;
  catalog_schema: Static<typeof InputFeatureSchema>[];
  start_date: string; // ISO date string
  end_date: string; // ISO date string
  start_at?: number; // Unix timestamp in seconds
  chunk_size_days?: number; // Optional, defaults to 30 in Python
}

export interface CatalogTrainModelConfig {
  model_output_path: string;
  training_matrix_dir: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  catalog_schema: Static<typeof InputFeatureSchema>[];
  start_at?: number; // Unix timestamp in seconds
}

export interface CatalogTrainModelMetadata {
  config: Record<string, any>;
  metrics?: Record<string, any>;
}

// `event_identifier` must match an entry in CatalogInferenceConfig.events;
// the python handler resolves it back to that event's cached parquet.
export interface CatalogInferenceSuppressionFilter {
  event_identifier: string;
  subconditions?: DecisionEngineCondition;
}

export interface CatalogInferenceConfig {
  model_input_path: string;
  inference_output_path: string;
  users_file_path: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  catalog_file_path: string;
  catalog_schema: Static<typeof InputFeatureSchema>[];
  events: CatalogKeyEventConfig[];
  inference_batch_size?: number;
  top_k?: number;
  lookback_days?: number;
  start_at?: number; // Unix timestamp in seconds
  collection_filter?: DecisionEngineCondition; // Per-user collection filter
  suppression_filter?: CatalogInferenceSuppressionFilter;
}

export interface CatalogInferenceMetadata {
  config: Record<string, any>;
  num_users: number;
  num_recommendations: number;
  top_k: number;
  metrics?: Record<string, any>;
}

// Lookalike audience types
export interface LookalikeTrainConfig {
  feature_key_events: KeyEventConfig[];
  users_file_path: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  base_audience_users_file_path: string;
  model_output_path: string;
  pca_components?: number;
  start_at?: number;
}

export interface LookalikeTrainMetadata {
  config: Record<string, any>;
  training_date: string;
  metrics?: Record<string, any>;
  num_base_audience_users?: number;
  num_eligible_users?: number;
  num_features?: number;
  num_pca_components?: number;
  auc?: number;
  precision_at_k?: Record<string, number>;
  lift_at_k?: Record<string, number>;
  quantiles?: Quantile[];
}

export interface LookalikeInferenceConfig {
  model_input_path: string;
  inference_output_path: string;
  users_file_path: string;
  user_schema: Static<typeof InputFeatureSchema>[];
  feature_key_events: KeyEventConfig[];
  inference_batch_size?: number;
  start_at?: number;
}

export interface LookalikeInferenceMetadata {
  config: Record<string, any>;
  num_users: number;
  metrics?: Record<string, any>;
}
